/* eslint-disable @bigbinary/neeto/use-standard-date-time-formats */
import { toLabelAndValue, isNotPresent, isNotEmpty } from "neetocist";
import { uniq, pluck, difference, identity } from "ramda";

import {
  END_OF_DAY,
  SLOT_TIME_FORMAT_24H,
  SLOT_TIME_FORMAT,
} from "./constants";

import { DEFAULT_HOUR_FORMAT } from "../constants";
import dayjs from "../dayjsExtended";
import { convert24hTo12h, isTimeBehind } from "../utils";

const buildSlotIntervals = ({
  interval,
  bookedSlots,
  hourFormat = DEFAULT_HOUR_FORMAT,
}) => {
  const allIntervalsForADay = dayjs.timeIntervalsForDay(interval);
  allIntervalsForADay.push(END_OF_DAY);

  return (slotType, selectedValue = null) => {
    const uniqBookedSlots = uniq(pluck(slotType, bookedSlots));
    const slotsAvailableForBooking = difference(
      allIntervalsForADay,
      uniqBookedSlots
    );
    let slots;
    if (hourFormat === "12h") {
      slots = slotsAvailableForBooking.map(value => ({
        value,
        label: convert24hTo12h(value),
      }));
    } else {
      slots = slotsAvailableForBooking.map(toLabelAndValue);
    }

    if (!selectedValue) return slots;

    const selectedSlot = {
      value: selectedValue,
      label:
        hourFormat === "12h" ? convert24hTo12h(selectedValue) : selectedValue,
    };

    return sortSlots([selectedSlot, ...slots]);
  };
};

const sortSlots = slots =>
  slots.sort((a, b) => (isTimeBehind(a["value"], b["value"]) ? -1 : 1));

const slotsSelectComponentDefaultValue = slot =>
  slot ? toLabelAndValue(slot) : "";

const padZeroes = str => (str.length % 2 === 0 ? str : `0${str}`);

const filterSlots = (option, searchTerm) => {
  if (isNotPresent(searchTerm) || option.data.__isNew__) return true;

  const searchDigits = searchTerm.replaceAll(/\D+/gi, "");
  const searchLetters = searchTerm.toLowerCase().replaceAll(/[^a-z]/gi, "");
  const paddedDigits = padZeroes(searchDigits);

  const label = (option.label || option.value).toLowerCase();
  const labelDigits = label.replaceAll(/\D+/gi, "");

  const doesNumberMatch =
    option.value.replace(":", "").startsWith(paddedDigits) ||
    labelDigits.startsWith(paddedDigits);

  const doesAmPmMatch = !searchLetters || label.includes(searchLetters);

  return doesNumberMatch && doesAmPmMatch;
};

const formatCreateLabel = identity;

const parseLooseTime = value => {
  if (!value || typeof value !== "string") return null;

  const trimmed = value.trim();

  // Try strict 24h format (e.g. "14:30")
  const parsed24h = dayjs(trimmed, SLOT_TIME_FORMAT_24H, true);
  if (parsed24h.isValid()) return parsed24h;

  // Try strict 12h format (e.g. "03:00 PM")
  const parsed12h = dayjs(trimmed, SLOT_TIME_FORMAT, true);
  if (parsed12h.isValid()) return parsed12h;

  // Try loose format: "3 pm", "3pm", "3:30pm", "3:30 PM", etc.
  const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a|p)$/i);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const meridiem = match[3].toLowerCase();

  if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return null;

  if (meridiem.startsWith("p") && hours !== 12) hours += 12;

  if (meridiem.startsWith("a") && hours === 12) hours = 0;

  return dayjs().hour(hours).minute(minutes).second(0);
};

const isValidTimeSlotOption = value => parseLooseTime(value) !== null;

const formatTimeSlotOption = v => {
  let value = v || "";
  if (isNotEmpty(value)) {
    const parsed = parseLooseTime(value);
    if (parsed) {
      value = parsed.format(SLOT_TIME_FORMAT_24H);
    } else if (value[1] === ":") {
      // Pad hour with 0 if single digit (legacy logic)
      value = `0${value}`;
    }
  }

  return value;
};

const roundToNearestHour = time => {
  const minutes = time.minute();

  if (minutes === 59) return time.startOf("hour").add(1, "hour");

  return time.add(1, "hour");
};

const generateNewPeriod = (periods, wday) => {
  const lastEndTime = periods[periods.length - 1].endTime;
  const newStartTime = roundToNearestHour(
    dayjs(lastEndTime, SLOT_TIME_FORMAT_24H)
  );
  const newEndTime = dayjs(newStartTime, SLOT_TIME_FORMAT_24H).add(1, "hour");

  return {
    startTime: dayjs(newStartTime).format(SLOT_TIME_FORMAT_24H),
    endTime: dayjs(newEndTime).format(SLOT_TIME_FORMAT_24H),
    wday,
  };
};

const addDefaultPeriod = wday => ({
  wday,
  startTime: dayjs().hour(9).minute(0).format(SLOT_TIME_FORMAT_24H),
  endTime: dayjs().hour(17).minute(0).format(SLOT_TIME_FORMAT_24H),
});

export {
  buildSlotIntervals,
  slotsSelectComponentDefaultValue,
  filterSlots,
  generateNewPeriod,
  addDefaultPeriod,
  isValidTimeSlotOption,
  formatTimeSlotOption,
  formatCreateLabel,
};
